/*
 * Choosing a part out of the register, for the work order's material dialog.
 *
 * Everything here is a pure function of its arguments so the rules can be tested without
 * rendering a dialog. Shaped like features/cmms/toolServiceStatus.js.
 *
 * Lives in features/cmmsSpareParts rather than in the work order feature because the subject is
 * the register: the work order is the first caller, not the owner.
 */
import { formatQuantity } from "../cmms/cmmsFormat";
import messages from "./messages";

/*
 * Options for AxionAutocomplete, which wants { id, title }.
 *
 * Retired parts are left out. MaterialUsageService refuses them with
 * CMMS_SPARE_PART_IS_INACTIVE, so offering one is offering a guaranteed 400 — and the reason
 * retiring exists is that history keeps its subject, not that a retired number goes on
 * collecting new consumption.
 *
 * The quantity is in the label because the moment of choosing is the moment it matters: a
 * technician booking three of something wants to know there are two. It is what the register
 * answered when this list loaded, so it informs the choice and never gates it.
 */
export const buildSparePartOptions = (spareParts, intl) =>
  (spareParts ?? [])
    .filter((part) => part && part.isActive !== false)
    .map((part) => ({
      id: part.id,
      title: intl.formatMessage(messages.pickerOptionLabel, {
        partNumber: part.partNumber,
        name: part.name,
        quantity: formatQuantity(intl, part.quantityOnHand ?? 0, 2),
        unit: part.unit ?? "",
      }),
    }));

/** The option for an id, or null. AxionAutocomplete is controlled by the option, not the id. */
export const findSparePartOption = (options, sparePartId) =>
  (options ?? []).find((option) => option.id === sparePartId) ?? null;

/*
 * How many are missing if this quantity is booked, or null when nothing is.
 *
 * Drives an advisory warning and nothing else. Booking is never blocked: the shelf can be wrong
 * — which is what a stocktake exists to settle — and refusing to record what was actually used
 * would corrupt the history to protect a number.
 *
 * Exactly enough is not a shortfall. Taking the last seven of seven is an ordinary day, and
 * warning about it would teach people to click past the warning that matters.
 */
export const getStockShortfall = (sparePart, quantity) => {
  if (!sparePart) return null;

  const wanted = Number(quantity);
  if (quantity === "" || quantity === null || quantity === undefined || Number.isNaN(wanted)) {
    return null;
  }

  const onHand = Number(sparePart.quantityOnHand ?? 0);
  const shortfall = wanted - onHand;

  return shortfall > 0 ? shortfall : null;
};
